// D4 — client-side image downscale/compress before an image ever enters a chat
// payload. The old path shipped a full-resolution phone photo (~12MP → ~10 MB
// base64) into React state and the request body with no resize, which locked the
// UI for ~a minute. Downscaling to a sane ceiling (2048px longest edge, JPEG
// ~0.85) shrinks the payload ~10-20× and moves decode work off the main thread
// via createImageBitmap.

export const DEFAULT_MAX_EDGE = 2048;
export const DEFAULT_QUALITY = 0.85;

export interface DownscaleOptions {
  maxEdge?: number;
  quality?: number;
}

export interface DownscaledImage {
  /** Raw base64 (no `data:` prefix) — matches the existing selectedImage contract. */
  base64: string;
  /** Full data URL, for an immediate optimistic preview thumbnail. */
  dataUrl: string;
  /** Always image/jpeg — we re-encode on the canvas. */
  type: string;
  width: number;
  height: number;
}

/**
 * Fit (width, height) inside a maxEdge × maxEdge box, preserving aspect ratio.
 * Never upscales — images already within the ceiling pass through unchanged.
 * Pure and deterministic; this is the unit under test.
 */
export function computeScaledDimensions(
  width: number,
  height: number,
  maxEdge: number = DEFAULT_MAX_EDGE
): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const longest = Math.max(width, height);
  if (longest <= maxEdge) {
    return { width: Math.round(width), height: Math.round(height) };
  }
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Decode → downscale → JPEG-encode an image file entirely on the client.
 * Async throughout (createImageBitmap decodes off the interaction path), so the
 * UI stays responsive. Returns raw base64 + a data URL for the preview.
 */
export async function downscaleImage(
  file: Blob,
  opts: DownscaleOptions = {}
): Promise<DownscaledImage> {
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = opts.quality ?? DEFAULT_QUALITY;

  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = computeScaledDimensions(bitmap.width, bitmap.height, maxEdge);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.drawImage(bitmap, 0, 0, width, height);

    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
    return { base64, dataUrl, type: 'image/jpeg', width, height };
  } finally {
    bitmap.close?.();
  }
}
